import React, { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { Skeleton, TableCell, Tooltip } from "@mui/material";
import Tab from "react-bootstrap/Tab";
import moment from "moment";
import { Link } from "react-router-dom";
import { EncounterHistoryTitle, noDataAvailText } from "../Constants";
import { DateSelectorWithoutFormik } from "../../../common/textfield/DateSelector";
import { searchEncounters } from "../../api/Encounter";
import CustomPagination from "../../../common/components/Pagination/CustomPagination";
import { getPatientData } from "../../../api/Individual";
import { failed } from "../../../common/Toastify";
import ViewIcon from "../../../../assets/images/view.png";
import { ViewDateTime } from "../../../../utils/DateSupport";
import { useSelector } from "react-redux";

// Default lookback window for the patient dashboard's encounter history list.
const DEFAULT_ENCOUNTER_RANGE_MONTHS = 3;
const ROWS_PER_PAGE = 5;
const DATE_FORMAT = "MM-DD-YYYY";

export function EncounterHistoryList({ patientId }) {
    const [encounterStartDate, setEncounterStartDate] = useState(
        moment().subtract(DEFAULT_ENCOUNTER_RANGE_MONTHS, "months").format(DATE_FORMAT)
    );
    const [encounterEndDate, setEncounterEndDate] = useState(
        moment().format(DATE_FORMAT)
    );
    const [encounterData, setEncounterData] = useState([]);
    const [encounterCount, setEncounterCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [loadedKey, setLoadedKey] = useState("");
    const [patientRecord, setPatientRecord] = useState({ patientId, data: null, loaded: false });
    const userOrgId = useSelector((state) => state?.auth?.user?.organizations?.[0]?.id);
    const isSuperAdmin = useSelector((state) => state?.auth?.user?.["cognito:groups"]?.includes("Super-Admin"));

    if (patientRecord.patientId !== patientId) {
        setPatientRecord({ patientId, data: null, loaded: false });
    }
    const patientData = patientRecord.patientId === patientId ? patientRecord.data : null;
    const patientLoaded = patientRecord.patientId === patientId && patientRecord.loaded;
    const patientOrgId = patientData?.managingOrganization?.[0]?.reference?.split("/")?.[1];
    const orgId = isSuperAdmin ? patientOrgId : userOrgId;
    const rangeKey = `${patientId}|${encounterStartDate}|${encounterEndDate}`;
    const [listPage, setListPage] = useState({ rangeKey, page: 0 });
    if (listPage.rangeKey !== rangeKey) {
        setListPage({ rangeKey, page: 0 });
    }
    const page = listPage.rangeKey === rangeKey ? listPage.page : 0;
    const requestKey = `${rangeKey}|${page}|${orgId || ""}`;
    const waitingForPatientOrg = isSuperAdmin && !patientLoaded;
    const showLoading = waitingForPatientOrg || isLoading || loadedKey !== requestKey;

    useEffect(() => {
        let cancelled = false;
        getPatientData(patientId)
            .then((response) => {
                if (cancelled) return;
                setPatientRecord({ patientId, data: response?.result ?? null, loaded: true });
            })
            .catch((err) => {
                if (cancelled) return;
                setPatientRecord({ patientId, data: null, loaded: true });
                failed(err?.response?.data?.message || err?.response?.data?.error || err.message);
            });
        return () => {
            cancelled = true;
        };
    }, [patientId]);

    useEffect(() => {
        if (waitingForPatientOrg) return undefined;
        if (!orgId || !patientId) {
            setEncounterData([]);
            setEncounterCount(0);
            setIsLoading(false);
            setLoadedKey(requestKey);
            return undefined;
        }

        let cancelled = false;
        setIsLoading(true);
        searchEncounters({
            orgId,
            patientId,
            start: moment(encounterStartDate, DATE_FORMAT).startOf("day").valueOf(),
            end: moment(encounterEndDate, DATE_FORMAT).endOf("day").valueOf(),
            status: "finished",
            type: "closed",
            page: page + 1,
            pageSize: ROWS_PER_PAGE,
        })
            .then((res) => {
                if (cancelled) return;
                setEncounterData(Array.isArray(res?.data) ? res.data : []);
                setEncounterCount(res?.count?.Count || 0);
            })
            .catch((err) => {
                if (cancelled) return;
                setEncounterData([]);
                setEncounterCount(0);
                failed(err?.response?.data?.message || err?.response?.data?.error || err.message);
            })
            .finally(() => {
                if (cancelled) return;
                setIsLoading(false);
                setLoadedKey(requestKey);
            });

        return () => {
            cancelled = true;
        };
    }, [waitingForPatientOrg, orgId, patientId, encounterStartDate, encounterEndDate, page, requestKey]);

    return (
        <Tab.Pane eventKey="encounters">
            <div className="mnth-date-wrap">
                <DateSelectorWithoutFormik
                    label="Start Date"
                    minDate=""
                    maxDate={moment(encounterEndDate, DATE_FORMAT)}
                    value={moment(encounterStartDate, DATE_FORMAT)}
                    handleChange={(value) => setEncounterStartDate(value.format(DATE_FORMAT))}
                    type="readonly"
                />
                <DateSelectorWithoutFormik
                    label="End Date"
                    minDate={moment(encounterStartDate, DATE_FORMAT)}
                    maxDate={moment()}
                    value={moment(encounterEndDate, DATE_FORMAT)}
                    handleChange={(value) => setEncounterEndDate(value.format(DATE_FORMAT))}
                    type="readonly"
                />
            </div>
            <div className="table-wrap">
                <Table responsive>
                    <thead>
                        <tr>
                            {EncounterHistoryTitle.map((title) => <th key={title}>{title}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {showLoading ? (
                            [1, 2, 3, 4, 5, 6].map((row) => (
                                <tr key={row}>
                                    {EncounterHistoryTitle.map((title) => (
                                        <td key={title}><Skeleton animation="wave" /></td>
                                    ))}
                                </tr>
                            ))
                        ) : encounterData?.length > 0 ? (
                            encounterData.map(({ id, period, participant }) => (
                                <tr key={id}>
                                    <td>{ViewDateTime(period?.end)}</td>
                                    <td>{id || "-"}</td>
                                    <td>
                                        {participant?.at(-1)?.individual?.display
                                            ?.split(" ")
                                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                            .join(" ") || "-"}
                                    </td>
                                    <td>
                                        <div className="action-wrap" style={{ justifyContent: "start" }}>
                                            <Tooltip title="View">
                                                <Link
                                                    to="/app/encounter-details"
                                                    state={{ encounterId: id, from: "pastEncounter" }}
                                                    className="view-btn"
                                                >
                                                    <img src={ViewIcon} alt="View" />
                                                </Link>
                                            </Tooltip>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <TableCell colSpan={EncounterHistoryTitle.length} style={{ textAlign: "center" }}>
                                    {noDataAvailText}
                                </TableCell>
                            </tr>
                        )}
                    </tbody>
                </Table>
                {!showLoading && encounterCount > 0 && (
                    <CustomPagination
                        tableData={encounterData}
                        totalRows={encounterCount}
                        rowsPerPage={ROWS_PER_PAGE}
                        page={page}
                        handlePage={(nextPage) => setListPage({ rangeKey, page: nextPage })}
                    />
                )}
            </div>
        </Tab.Pane>
    );
}
